import type { Answer, AttributeKey, Destination, Question, SunType } from "./quiz-data";

export type AnswerMap = Record<string, Answer>;

export function filterBySeason(destinations: Destination[], answers: AnswerMap): Destination[] {
  const seasonAnswer = answers["season"];
  if (!seasonAnswer || seasonAnswer.seasonFilter === "any") return destinations;
  return destinations.filter((d) => d.seasonType === seasonAnswer.seasonFilter);
}

export function filterBySunType(destinations: Destination[], answers: AnswerMap): Destination[] {
  const sunTypeAnswer = answers["sun-type"];
  if (!sunTypeAnswer?.sunType) return destinations;
  const chosen: SunType = sunTypeAnswer.sunType;
  return destinations.filter((d) => {
    if (d.seasonType !== "sun") return true;
    return d.sunCategory === chosen;
  });
}

export function computeRawScore(
  destination: Destination,
  answers: AnswerMap,
  questions: Question[]
): { raw: number; max: number } {
  let raw = 0;
  let max = 0;
  for (const q of questions) {
    const a = answers[q.id];
    if (!a?.targets) continue;
    const multiplier = q.doubleWeight ? 2 : 1;
    for (const t of a.targets) {
      const actual = destination.attributes[t.attribute];
      const similarity = 1 - Math.abs(t.ideal - actual) / 10;
      raw += similarity * t.weight * multiplier;
      max += t.weight * multiplier;
    }
  }
  return { raw, max };
}

export type RankedDestination = {
  destination: Destination;
  matchPercent: number;
  liveJobs: number;
};

export function rankDestinations(
  destinations: Destination[],
  answers: AnswerMap,
  questions: Question[],
  jobCounts: Record<string, number>
): RankedDestination[] {
  let pool = filterBySeason(destinations, answers);
  pool = filterBySunType(pool, answers);

  const scored: RankedDestination[] = pool.map((d) => {
    const { raw, max } = computeRawScore(d, answers, questions);
    const pct = max > 0 ? Math.max(0, Math.round((raw / max) * 100)) : 0;
    return { destination: d, matchPercent: pct, liveJobs: jobCounts[d.id] ?? 0 };
  });

  scored.sort((a, b) => {
    if (Math.abs(a.matchPercent - b.matchPercent) <= 2) {
      return b.liveJobs - a.liveJobs || b.matchPercent - a.matchPercent;
    }
    return b.matchPercent - a.matchPercent;
  });

  return scored;
}

export function getUserTopAxes(
  answers: AnswerMap,
  questions: Question[]
): AttributeKey[] {
  const weights: Partial<Record<AttributeKey, number>> = {};
  for (const q of questions) {
    const a = answers[q.id];
    if (!a?.targets) continue;
    const multiplier = q.doubleWeight ? 2 : 1;
    for (const t of a.targets) {
      weights[t.attribute] = (weights[t.attribute] ?? 0) + t.weight * multiplier;
    }
  }
  return (Object.entries(weights) as [AttributeKey, number][])
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key);
}

export function selectPitchline(
  destination: Destination,
  answers: AnswerMap,
  questions: Question[]
): string {
  const topAxes = getUserTopAxes(answers, questions);

  for (const axis of topAxes) {
    if (destination.pitchlines[axis]) return destination.pitchlines[axis];
  }

  const highestAttr = (Object.entries(destination.attributes) as [AttributeKey, number][])
    .sort((a, b) => b[1] - a[1])[0]?.[0];
  if (highestAttr && destination.pitchlines[highestAttr]) {
    return destination.pitchlines[highestAttr];
  }

  const firstPitchline = Object.values(destination.pitchlines)[0];
  return firstPitchline ?? `${destination.name} is a strong match for what you're after.`;
}
